import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface PageVisibilityRow {
  path: string;
  published: boolean;
  deleted: boolean;
}

function normalizePath(path: string) {
  if (!path) return "/";
  if (path === "/") return "/";
  return path.endsWith("/") ? path.slice(0, -1) : path;
}

export function usePageVisibility() {
  return useQuery({
    queryKey: ["page-visibility"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("manage-page-visibility", {
        body: { operation: "list" },
      });

      if (error) throw error;

      const pages = Array.isArray(data?.pages) ? data.pages : [];
      return pages
        .filter((item: unknown): item is PageVisibilityRow => {
          if (!item || typeof item !== "object") return false;
          const row = item as Record<string, unknown>;
          return typeof row.path === "string";
        })
        .map((row) => ({
          path: normalizePath(row.path),
          published: row.published !== false,
          deleted: row.deleted === true,
        }));
    },
    staleTime: 15_000,
    retry: 1,
  });
}

export function useIsPageVisible(path: string) {
  const normalizedPath = normalizePath(path);
  const query = usePageVisibility();

  const row = useMemo(
    () => query.data?.find((item) => item.path === normalizedPath),
    [query.data, normalizedPath],
  );

  const isVisible = row ? row.published && !row.deleted : true;

  return {
    isVisible,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
